import { EngagementV1DTO } from '@beable/roster-backend-client-v1';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { findSetLocalization, getEngagementIcon } from './types';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StyledList = styled(List)(({ theme }) => ({
    width: '100%',
    background: theme.palette.background.paper,
    position: 'relative',
    overflow: 'auto',
    maxHeight: '100vh'
}));

const StyledListItem = styled(ListItem)(() => ({
    borderBottom: '2px solid #f8f3ff'
}));

const MonthAvatar = styled(Typography)(() => ({
    background: '#754dac',
    fontSize: '12px',
    fontWeight: 'bold',
    marginTop: '8px',
    color: '#ffffff',
    borderRadius: '50%',
    height: '40px',
    width: '40px',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginRight: '8px'
})) as typeof Typography;
interface EngagementListProps {
    engagements: EngagementV1DTO[];
    onSelect: (engagement: EngagementV1DTO) => void;
    selected: { id: string } | null;
    showUnpublished?: boolean;
}
export default function EngagementList({ engagements, onSelect, selected, showUnpublished = true }: EngagementListProps) {
    const itemRef = useRef<null | HTMLLIElement>(null);
    useEffect(() => {
        if (!selected || itemRef.current === null) {
            return;
        }
        itemRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
        });
    }, [selected]);
    const filteredEngagements = useMemo(() => {
        return showUnpublished ? engagements : engagements.filter(e => e.active === true);
    }, [engagements, showUnpublished]);
    
    const memoOnSelect = useCallback(onSelect, []);

    return (
        <StyledList>
            {filteredEngagements.map((engagement) => {
                const labelId = `checkbox-list-label-${engagement.id}`;
                const localization = findSetLocalization(engagement);
                const icon = getEngagementIcon(engagement.icon);
                const isSelected = selected?.id === engagement.id;

                return (
                    <StyledListItem
                        key={engagement.id}
                        ref={isSelected ? itemRef : null}
                        disablePadding
                    >
                        <ListItemButton
                            onClick={() => {
                                memoOnSelect(engagement);
                            }}
                            selected={isSelected}
                        >
                            <ListItemIcon>
                                {icon}
                            </ListItemIcon>
                            <ListItemText
                                id={labelId}
                                primary={localization?.title || 'No Title Set'}
                                secondary={
                                    <>
                                        <Typography
                                            component="span"
                                            variant="body2"
                                            sx={{
                                                color: "text.primary",
                                                display: 'inline'
                                            }}>
                                            {localization?.subtext}
                                        </Typography>
                                        <>
                                            {engagement.schedules.map((schedule) => {
                                                return <Typography component="span" sx={{ display: 'flex', padding: '5px 0px', gap: '1' }} key={`${schedule.id}-dates`}>
                                                    <MonthAvatar component="span">
                                                        {new Date(schedule.startAt).toLocaleDateString(undefined, { month: "short" })}
                                                    </MonthAvatar>
                                                    <Typography component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span>
                                                            {`${new Date(schedule.startAt).toLocaleDateString()} 
                                                            to 
                                                            ${ schedule.endAt ? new Date(schedule.endAt).toLocaleDateString()  : '⚠'}`
                                                            }
                                                        </span>
                                                        <span>
                                                            {schedule.allSites ? <Chip component='span' label="All Sites"/> : <></>}
                                                            {schedule.excludeSiteCount ? <Chip component='span' label={`${schedule.excludeSiteCount} site(s) excluded`}/> : <></>}
                                                            {schedule.includeSiteCount ? <Chip component='span' label={`${schedule.includeSiteCount} site(s) included`}/> : <></>}
                                                        </span>
                                                    </Typography>
                                                </Typography>
                                            })}
                                        </>
                                    </>
                                }
                            />
                        </ListItemButton>
                        <ListItemSecondaryAction>
                            <IconButton disabled >
                                {engagement.active ? <CheckCircleIcon /> : <UnpublishedIcon />}
                            </IconButton>
                        </ListItemSecondaryAction>
                    </StyledListItem>
                );
            })}
        </StyledList>
    );
}